/* /import — bulk create tasks or assortment matrices from a file.
 *
 * No mockup exists for this screen. It is spec/03 **archetype B** applied to a create-only record:
 * page head → `dp-grid` of form + reference rail → bottom save bar that exists only while there is
 * something to submit. There is no view mode and no id, so there is no `?edit=1` and no dirty
 * comparison — `canSave` is simply "are there rows".
 *
 * It replaces four MUI tabs (`src/module/tasks-import/`, 1,419 lines). Three of them were the same
 * flow copy-pasted; the fourth is now `/uploads`.
 *
 * Two things this page does that the one it replaces did not:
 *
 * 1. **It shows you the rows before it sends them.** The old page enabled Save the moment a file
 *    parsed and posted blind; the only feedback was the API's `details` string in a red box.
 *    Validation here is SHAPE ONLY and the page says so — point, network, product and template
 *    codes exist in the database, not in the file, so only the server can resolve them.
 * 2. **It hands you off to the result.** Both endpoints are ASYNCHRONOUS: they answer
 *    `{action, status: "pending"}` and Celery does the work. The old page showed a green
 *    "Items successfully saved" toast and stopped, so a batch where every row failed looked
 *    identical to one that worked. Submitting now lands on `/uploads`, which is where the outcome
 *    actually lives.
 */

import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { Button, Card, DatePicker, Descriptions, Empty, Flex, Form, Input, Modal, Segmented, Space, Table, Tag, Tooltip, Typography, Upload } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClusterOutlined,
  DeleteOutlined,
  DownloadOutlined,
  InboxOutlined,
  OrderedListOutlined,
  PlusOutlined,
  PushpinOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import { StatusIcon, useTableScrollY } from "../../component/table-cells";
import { ENTITY_DROPDOWN_SETTINGS, IMPORT } from "../../config/lang";
import EntitySelect from "../../component/EntitySelect";
import { downloadCsv, readTable } from "./csv";
import { DATE_FORMAT, KINDS, asDate, getKind, templateRows } from "./kinds";
import { httpRequest } from "../../util/action";
import { fill, translate } from "../../util/util";

import "./import.scss";

let rowSeq = 0;
const withKey = row => ({ ...row, key: `r${++rowSeq}` });

const Import = () => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language) || "en";

  const t = key => translate(IMPORT, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);

  /* The kind lives in the URL so a bookmark, a Back, or a link from a colleague lands on the same
     importer rather than always on the first one. */
  const initialKind = useRef(new URLSearchParams(location.search).get("kind")).current;
  const [kindKey, setKindKey] = useState(() => (KINDS.some(k => k.key === initialKind) ? initialKind : KINDS[0].key));
  const kind = getKind(kindKey);

  const [form, setForm] = useState({ name: "", template: null, scopeType: "network", scope: null });
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  /* A 400-row file puts its problems on page 8 of the preview, where nobody will find them. */
  const [onlyBad, setOnlyBad] = useState(false);
  /* Shown against the fields only after a refused submit — flagging an empty form the moment it
     opens tells the user off for not having filled it in yet. */
  const [showFormErrors, setShowFormErrors] = useState(false);

  /* Set just before an intentional navigation, so the unsaved-rows guard lets it through. */
  const leaving = useRef(false);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  /* ------------------------------------------------------------ validation */

  /** A row error code → its sentence. `e_date` names the format rather than just refusing. */
  const msg = code => (code === "e_date" ? fill(t("e_date"), { f: DATE_FORMAT }) : t(code));

  /* `n` is the row's position in the FILE, carried on the row itself — the preview can be filtered
     to the problems, and a number derived from the render index would then renumber them. */
  const checked = useMemo(() => rows.map((row, index) => ({ ...row, n: index + 1, errors: kind.validate(row) })), [rows, kind]);
  const okRows = useMemo(() => checked.filter(row => !Object.keys(row.errors).length), [checked]);
  const badCount = checked.length - okRows.length;
  const visible = useMemo(() => (onlyBad ? checked.filter(row => Object.keys(row.errors).length) : checked), [checked, onlyBad]);

  const formErrors = useMemo(() => {
    const errors = {};
    if (!form.name.trim()) errors.name = t("v_name_required");
    if (kind.needsTemplate && !form.template) errors.template = t("v_template_required");
    if (kind.needsScope && !form.scope) errors.scope = t("v_scope_required");
    return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, kind, lang]);

  /* ------------------------------------------------------------------ file */

  const clearRows = () => {
    setRows([]);
    setFile(null);
    setFileError(null);
  };

  const changeKind = next => {
    if (!rows.length) {
      setKindKey(next);
      history.replace({ search: next === KINDS[0].key ? "" : `?kind=${next}` });
      return;
    }
    /* The column contract differs per kind, so the parsed rows cannot carry over — and losing a
       400-row file to a mis-click on a segment would be the page's worst moment. */
    /* A static `Modal.confirm` renders outside the tree and does not inherit AppShell's
       `ConfigProvider locale`, so its buttons stay English unless they are passed. The verb names
       the action rather than saying OK (spec/04). */
    Modal.confirm({
      title: t("change_kind_confirm"),
      okText: t("discard_rows"),
      okButtonProps: { danger: true },
      cancelText: t("cancel"),
      onOk: () => {
        clearRows();
        setKindKey(next);
        history.replace({ search: next === KINDS[0].key ? "" : `?kind=${next}` });
      },
    });
  };

  const takeFile = async picked => {
    setReading(true);
    setFileError(null);
    try {
      const table = await readTable(picked);
      const parsed = kind.parse(table);
      if (!parsed.length) {
        setFileError(t("empty_file"));
        setFile(null);
        setRows([]);
      } else {
        setFile({ name: picked.name });
        setRows(parsed.map(withKey));
      }
    } catch (error) {
      console.error("import: could not read the file", error);
      setFileError(t("read_error"));
      setFile(null);
      setRows([]);
    }
    setReading(false);
  };

  /* ---------------------------------------------------------------- submit */

  const submit = () => {
    setShowFormErrors(true);
    const firstFormError = Object.values(formErrors)[0];
    if (firstFormError) {
      Alert.warning(firstFormError, { position: "top-right" });
      return;
    }
    if (!okRows.length) {
      Alert.warning(t("v_no_valid_rows"), { position: "top-right" });
      return;
    }

    setSubmitting(true);
    httpRequest({ method: "POST", url: kind.endpoint, post: kind.buildPayload(form, okRows) }, async response => {
      setSubmitting(false);
      /* `httpRequest` hands back a Response for 200/204/400 but a plain object for the failures it
         has already toasted — reading `.json()` off that would throw inside the callback. */
      if (typeof response?.json !== "function") return;

      let body = null;
      try {
        body = await response.json();
      } catch (error) {
        body = null;
      }

      if (response.status === 400) {
        /* `details` is always a plain string on both endpoints. */
        Alert.error(body?.details || t("submit_failed"), { position: "top-right" });
        return;
      }

      Alert.success(t("queued_msg"), { position: "top-right" });
      /* ⚠ Clearing the rows here is NOT enough to quiet `<Prompt>`. React batches the state
         updates, so the re-render happens after this callback returns — at `history.push` time the
         Prompt still holds `when={true}` from the last render, and a *successful* import asked
         "the rows will be lost, leave the page?". The ref is read inside `message`, which
         react-router calls at navigation time, so it is current. */
      leaving.current = true;
      setRows([]);
      setFile(null);
      history.push(body?.action ? `/uploads?new=${encodeURIComponent(body.action)}` : "/uploads");
    });
  };

  /* --------------------------------------------------------------- columns */

  const editRow = (key, field, value) => setRows(prev => prev.map(row => (row.key === key ? { ...row, [field]: value } : row)));

  const columns = useMemo(() => {
    const cells = kind.columns.map(column => ({
      key: column.key,
      dataIndex: column.key,
      title: t(column.label),
      width: column.width,
      ellipsis: column.type !== "date",
      align: column.type === "number" ? "right" : undefined,
      render: (value, row) => {
        const invalid = Boolean(row.errors[column.key]);

        if (kind.editable) {
          if (column.type === "date") {
            /* A cell that did not parse falls back to a text input carrying the RAW value. A
               `<DatePicker>` can only show a valid date, so an unparseable `31/02/2026` rendered as
               an empty field — the reason column said "not a date" while the offending text was
               invisible, which is the one thing the user needs to see to fix it. Typing over it
               re-parses through `asDate`, so the field heals back into a real date. */
            const parsed = asDate(value);
            if (!parsed && String(value ?? "").trim()) {
              return <Input value={String(value)} status="error" onChange={event => editRow(row.key, column.key, event.target.value)} />;
            }
            return (
              <DatePicker
                value={parsed}
                format={DATE_FORMAT}
                status={invalid ? "error" : undefined}
                allowClear={false}
                onChange={next => editRow(row.key, column.key, next)}
              />
            );
          }
          return (
            <Input value={String(value ?? "")} status={invalid ? "error" : undefined} onChange={event => editRow(row.key, column.key, event.target.value)} />
          );
        }

        if (column.type === "date") {
          const parsed = asDate(value);
          /* An unparseable cell shows the text that is actually in the file — that is the whole
             point of a preview, and blanking it would hide the thing that needs fixing. */
          return <span className={`lp-num ${invalid ? "im-cell--bad" : ""}`}>{parsed ? parsed.format(DATE_FORMAT) : String(value ?? "") || "—"}</span>;
        }
        if (column.type === "number") {
          return (
            <Tooltip title={(row.products || []).join(", ") || null}>
              <span className="lp-num">{value}</span>
            </Tooltip>
          );
        }
        return <Typography.Text ellipsis={{ tooltip: String(value ?? "") }}>{String(value ?? "") || "—"}</Typography.Text>;
      },
    }));

    return [
      {
        key: "state",
        title: "",
        width: 44,
        align: "center",
        fixed: "left",
        render: (value, row) => {
          const bad = Object.keys(row.errors).length > 0;
          return <StatusIcon Icon={bad ? CloseCircleOutlined : CheckCircleOutlined} tone={bad ? "no" : "ok"} label={bad ? t("n_problems") : t("n_ok")} />;
        },
      },
      { key: "n", dataIndex: "n", title: t("col_row"), width: 64, fixed: "left", render: value => <span className="lp-num">{value}</span> },
      ...cells,
      {
        key: "reason",
        title: t("col_reason"),
        width: 220,
        ellipsis: true,
        render: (value, row) => {
          const text = [...new Set(Object.values(row.errors).map(msg))].join(" · ");
          if (!text) return <span className="lp-dash">—</span>;
          return (
            <Typography.Text ellipsis={{ tooltip: text }} className="im-reason">
              {text}
            </Typography.Text>
          );
        },
      },
      ...(kind.editable
        ? [
            {
              key: "remove",
              title: "",
              width: 56,
              fixed: "right",
              render: (value, row) => (
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label={t("remove_row")}
                  onClick={() => setRows(prev => prev.filter(item => item.key !== row.key))}
                />
              ),
            },
          ]
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, lang]);

  const scrollY = useTableScrollY();
  const tableWidth = 44 + 64 + 220 + (kind.editable ? 56 : 0) + kind.columns.reduce((sum, column) => sum + column.width, 0);

  /* ------------------------------------------------------------------ view */

  return (
    <Flex vertical className="shell-content im">
      {/* A parsed file is real work — several minutes of somebody's spreadsheet — and nothing on
          this page has been persisted yet. */}
      {/* `message` as a FUNCTION: returning `true` lets the navigation through with no dialog,
          which is the documented escape hatch and the only one that can read a ref set moments
          before the push. */}
      <Prompt when={rows.length > 0} message={() => (leaving.current ? true : t("leave_confirm"))} />

      <div className="dp-head">
        <div className="dp-head__main">
          <div className="dp-head__titlerow">
            <Typography.Title level={1} className="dp-title">
              {t("page_title")}
            </Typography.Title>
          </div>
          <Typography.Paragraph type="secondary" className="im-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </div>
        <Button icon={<UploadOutlined />} onClick={() => history.push("/uploads")}>
          {t("to_uploads")}
        </Button>
      </div>

      <div className="dp-grid">
        {/* ⚠ `dp-form` goes on a REAL element. `<Form component={false}>` renders no DOM node at
            all, so a `className` on it is silently dropped — verified with
            `document.querySelectorAll(".dp-form").length === 0` — and the archetype's label weight
            and full-width controls never applied. (`points/ResourceForm.js` has the same latent
            bug; left alone here, flagged.) */}
        <div className="dp-main dp-form">
          <Card className="dp-section" variant="outlined" title={null}>
            <div className="dp-sec-head">
              <h2 className="dp-sec-title">{t("kind_label")}</h2>
            </div>
            {/* Three named things, one visible at a time — the control antd offers for exactly
                that. It scrolls rather than wraps below the breakpoint (spec/03). */}
            <Segmented
              className="im-kinds"
              value={kindKey}
              onChange={changeKind}
              options={KINDS.map(item => ({ value: item.key, label: t(item.label) }))}
              block
            />
            <Typography.Paragraph type="secondary" className="dp-sec-desc im-kind-desc">
              {t(kind.desc)}
            </Typography.Paragraph>
          </Card>

          <Form layout="vertical" component={false} colon={false}>
            <Card className="dp-section" variant="outlined">
              <div className="dp-sec-head">
                <h2 className="dp-sec-title">{t("sec_batch")}</h2>
                <p className="dp-sec-desc">{t("sec_batch_desc")}</p>
              </div>
              <Form.Item
                label={t("f_name")}
                required
                validateStatus={showFormErrors && formErrors.name ? "error" : undefined}
                help={showFormErrors ? formErrors.name : undefined}>
                <Input value={form.name} placeholder={t("f_name_ph")} onChange={event => setField("name", event.target.value)} />
              </Form.Item>

              {kind.needsTemplate ? (
                <Form.Item
                  label={td("template")}
                  required
                  validateStatus={showFormErrors && formErrors.template ? "error" : undefined}
                  help={showFormErrors ? formErrors.template : undefined}>
                  {/* The template is what the merchandiser will actually be asked for, so it is the
                      one field that changes what the imported tasks mean. */}
                  <EntitySelect entity="template" value={form.template} prefix={<OrderedListOutlined />} onChange={option => setField("template", option)} />
                </Form.Item>
              ) : null}
            </Card>

            {kind.needsScope ? (
              <Card className="dp-section" variant="outlined">
                <div className="dp-sec-head">
                  <h2 className="dp-sec-title">{t("sec_scope")}</h2>
                  <p className="dp-sec-desc">{t("sec_scope_desc")}</p>
                </div>
                <Form.Item label={t("f_scope_type")}>
                  <Segmented
                    value={form.scopeType}
                    onChange={next => setForm(prev => ({ ...prev, scopeType: next, scope: null }))}
                    options={[
                      { value: "network", label: t("scope_network") },
                      { value: "point", label: t("scope_point") },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label={form.scopeType === "network" ? t("scope_network") : t("scope_point")}
                  required
                  validateStatus={showFormErrors && formErrors.scope ? "error" : undefined}
                  help={showFormErrors ? formErrors.scope : undefined}>
                  {form.scopeType === "network" ? (
                    <EntitySelect
                      entity="network"
                      value={form.scope}
                      extraQuery={{ active: true }}
                      prefix={<ClusterOutlined />}
                      onChange={option => setField("scope", option)}
                    />
                  ) : (
                    <EntitySelect entity="point" value={form.scope} prefix={<PushpinOutlined />} onChange={option => setField("scope", option)} />
                  )}
                </Form.Item>
              </Card>
            ) : null}

            <Card className="dp-section" variant="outlined">
              <div className="dp-sec-head">
                <h2 className="dp-sec-title">{t("sec_file")}</h2>
                {/* Only where the file actually carries dates — the assortment matrix is codes and
                    ticks, and telling its author about a date format is noise. */}
                {kind.columns.some(column => column.type === "date") ? <p className="dp-sec-desc">{fill(t("date_format_hint"), { f: DATE_FORMAT })}</p> : null}
              </div>

              <Upload.Dragger
                className="im-drop"
                accept={kind.accept}
                maxCount={1}
                multiple={false}
                showUploadList={false}
                disabled={reading}
                /* `false` keeps antd from posting the file anywhere: it is parsed in the browser
                   and only the resulting rows are ever sent. */
                beforeUpload={picked => {
                  takeFile(picked);
                  return false;
                }}>
                <p className="im-drop__icon">
                  <InboxOutlined />
                </p>
                <p className="im-drop__title">{reading ? t("reading") : t("drop_hint")}</p>
                <p className="im-drop__sub">{t("drop_sub")}</p>
              </Upload.Dragger>

              {file ? (
                <Flex className="im-file" align="center" gap="small" wrap>
                  <Tag>{file.name}</Tag>
                  <Button type="text" size="small" icon={<DeleteOutlined />} onClick={clearRows}>
                    {t("file_remove")}
                  </Button>
                </Flex>
              ) : null}

              {fileError ? (
                <Typography.Paragraph className="im-fileerror" role="alert">
                  {fileError}
                </Typography.Paragraph>
              ) : null}

              <Flex className="im-cols" vertical gap={4}>
                <Typography.Text type="secondary">{t("expected_columns")}</Typography.Text>
                <Space size={[6, 6]} wrap>
                  {kind.key === "assortment_matrix" ? (
                    <Typography.Text type="secondary" className="im-cols__note">
                      {t("kind_matrix_desc")}
                    </Typography.Text>
                  ) : (
                    kind.columns.map(column => (
                      <Tag key={column.key} className="im-col">
                        {t(column.label)}
                        {column.required ? <span className="im-col__req"> *</span> : null}
                      </Tag>
                    ))
                  )}
                </Space>
                <span>
                  <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadCsv(templateRows(kind, t), `${kind.key}-template.csv`)}>
                    {t("download_template")}
                  </Button>
                </span>
              </Flex>
            </Card>
          </Form>

          <Card className="dp-section" variant="outlined">
            <div className="dp-sec-head">
              <h2 className="dp-sec-title">{t("sec_rows")}</h2>
              <p className="dp-sec-desc">{t("sec_rows_desc")}</p>
            </div>

            {checked.length ? (
              <Flex className="im-rows__bar" align="center" gap="small" wrap>
                <Tag.CheckableTag checked={onlyBad} onChange={setOnlyBad}>
                  <Space size={6}>
                    {t("only_problems")}
                    <span className="lp-num">{badCount}</span>
                  </Space>
                </Tag.CheckableTag>
              </Flex>
            ) : null}

            <Table
              className="lp-table__grid im-rows"
              rowKey="key"
              size="middle"
              columns={columns}
              dataSource={visible}
              scroll={{ x: tableWidth, y: scrollY }}
              rowClassName={row => (Object.keys(row.errors).length ? "lp-row im-row--bad" : "lp-row")}
              /* A spreadsheet of a few thousand rows is ordinary here, and antd does not
                 virtualise — without a page size the preview alone would put every one of them in
                 the DOM. The problems stay reachable through the filter above. */
              pagination={{ pageSize: 50, showSizeChanger: false, size: "small", hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Flex vertical gap={8} align="center">
                        <Typography.Text strong>{t("rows_empty_title")}</Typography.Text>
                        <Typography.Text type="secondary">{kind.editable ? t("rows_empty_desc") : t("rows_empty_file_desc")}</Typography.Text>
                      </Flex>
                    }
                  />
                ),
              }}
            />

            {kind.editable ? (
              <Flex className="im-rows__foot" align="center" gap="small">
                <Button icon={<PlusOutlined />} onClick={() => setRows(prev => [...prev, withKey(kind.emptyRow())])}>
                  {t("add_row")}
                </Button>
              </Flex>
            ) : null}
          </Card>
        </div>

        <aside className="dp-rail">
          <Card className="dp-card" variant="outlined">
            <Typography.Text strong>{t("rail_summary")}</Typography.Text>
            {/* Number over label — never `{n} rows`, which cannot be inflected across four
                locales (spec/04#counts). */}
            <Flex className="im-facts" gap="middle" wrap>
              {[
                ["n_parsed", checked.length],
                ["n_ok", okRows.length],
                ["n_problems", badCount],
              ].map(([key, value]) => (
                <Flex key={key} vertical className={`im-fact ${key === "n_problems" && value ? "im-fact--bad" : ""}`}>
                  <span className="im-fact__n">{value}</span>
                  <Typography.Text type="secondary" className="im-fact__label">
                    {t(key)}
                  </Typography.Text>
                </Flex>
              ))}
            </Flex>
            <Typography.Paragraph type="secondary" className="im-hint">
              {t("server_checks_hint")}
            </Typography.Paragraph>
          </Card>

          <Card className="dp-card" variant="outlined">
            <Typography.Text strong>{t("rail_target")}</Typography.Text>
            <Descriptions className="im-target" column={1} size="small" colon={false}>
              <Descriptions.Item label={t("f_name")}>{form.name.trim() || <span className="lp-dash">—</span>}</Descriptions.Item>
              <Descriptions.Item label={t("kind_label")}>{t(kind.label)}</Descriptions.Item>
              {kind.needsTemplate ? (
                <Descriptions.Item label={td("template")}>
                  {form.template?.name || form.template?.label || <span className="lp-dash">—</span>}
                </Descriptions.Item>
              ) : null}
              {kind.needsScope ? (
                <Descriptions.Item label={form.scopeType === "network" ? t("scope_network") : t("scope_point")}>
                  {form.scope?.name || form.scope?.label || <span className="lp-dash">—</span>}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </Card>
        </aside>

        {/* Only while there is something to send — the bar is the archetype's Save, and an empty
            page has nothing to save. */}
        {rows.length > 0 ? (
          <div className="dp-savebar">
            {/* Label, then two numbers — a phrase like "124 rows" cannot be inflected across four
                locales (spec/04#counts). */}
            <span className="dp-savebar__msg">
              {badCount ? <CloseCircleOutlined /> : null}
              {t("n_ok")}: <span className="lp-num">{okRows.length}</span> / <span className="lp-num">{checked.length}</span>
            </span>
            <div className="dp-savebar__acts">
              <Button onClick={clearRows}>{t("cancel")}</Button>
              <Button type="primary" loading={submitting} disabled={!okRows.length} onClick={submit}>
                {t("submit")}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Flex>
  );
};

export default Import;
